import React from 'react';

interface StockChartProps {
  data: Array<{date: string; incoming?: number; outgoing?: number; total?: number}>;
  type: 'incoming' | 'outgoing' | 'total';
  title: string;
  className?: string;
}

export const StockChart: React.FC<StockChartProps> = ({
  data,
  type,
  title,
  className = ''
}) => {
  const maxValue = Math.max(...data.map(d => d[type] || d.total || 0));
  const getColor = () => {
    switch (type) {
      case 'incoming': return 'text-green-600 bg-green-100 dark:bg-green-900/30';
      case 'outgoing': return 'text-red-600 bg-red-100 dark:bg-red-900/30';
      default: return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30';
    }
  };

  const getLineColor = () => {
    switch (type) {
      case 'incoming': return 'stroke-green-500';
      case 'outgoing': return 'stroke-red-500';
      default: return 'stroke-blue-500';
    }
  };

  const getFillColor = () => {
    switch (type) {
      case 'incoming': return 'fill-green-500/20';
      case 'outgoing': return 'fill-red-500/20';
      default: return 'fill-blue-500/20';
    }
  };

  const getPointColor = () => {
    switch (type) {
      case 'incoming': return 'fill-green-500';
      case 'outgoing': return 'fill-red-500';
      default: return 'fill-blue-500';
    }
  };

  const generatePath = (points: Array<{x: number; y: number}>) => {
    if (points.length === 0) return '';

    return points.map((point, index) =>
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ');
  };

  const chartWidth = 100;
  const chartHeight = 100;
  const padding = 5;

  const chartData = data.slice(-30);
  const points = chartData.map((point, index) => {
    const value = point[type] || point.total || 0;
    const x = padding + (index / (chartData.length - 1 || 1)) * (chartWidth - 2 * padding);
    const y = chartHeight - padding - ((value / (maxValue || 1)) * (chartHeight - 2 * padding));
    return { x, y, value, date: point.date };
  });

  const areaPath = points.length > 0
    ? `${generatePath(points)} L ${points[points.length - 1].x} ${chartHeight - padding} L ${points[0].x} ${chartHeight - padding} Z`
    : '';

  return (
    <div className={`card p-6 ${className}`}>
      <h3 className={`text-lg font-semibold mb-4 ${getColor()}`}>
        📈 {title}
      </h3>

      {data.length === 0 || data.every(d => (d[type] || d.total || 0) === 0) ? (
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-16 h-16 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No activity yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {type === 'incoming' && 'Create incoming shipments to see trends'}
            {type === 'outgoing' && 'Process outgoing shipments to see trends'}
            {type === 'total' && 'Add inventory items to track changes'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>Last 30 Days</span>
            <span>Max: {maxValue}</span>
          </div>

          <div className="relative h-32 w-full">
            <svg
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              className="w-full h-full"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id={`gradient-${type}`} x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" className={getFillColor()} stopOpacity="0.2" />
                  <stop offset="100%" className={getFillColor()} stopOpacity="0.02" />
                </linearGradient>
              </defs>

              {points.length > 0 && (
                <>
                  <path
                    d={areaPath}
                    fill={`url(#gradient-${type})`}
                    className="transition-all duration-300"
                  />

                  <path
                    d={generatePath(points)}
                    fill="none"
                    className={`${getLineColor()} transition-all duration-300`}
                    strokeWidth="0.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {points.map((point, index) => (
                    <g key={index}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="1"
                        className="fill-white dark:fill-gray-800 transition-all duration-300"
                      />
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="0.8"
                        className={`${getPointColor()} transition-all duration-300 cursor-pointer hover:r-[1]`}
                      >
                        <title>{`${point.date}: ${point.value}`}</title>
                      </circle>
                    </g>
                  ))}
                </>
              )}
            </svg>
          </div>

          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{chartData[0]?.date}</span>
            <span>{chartData[chartData.length - 1]?.date}</span>
          </div>
        </div>
      )}
    </div>
  );
};
